import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Wand2, ArrowLeft, Save, Undo2, Redo2, Sparkles, Download, Eye, Film, Layers, Loader2, Languages, FileDown, FileUp, RotateCcw, Gauge, Check, FileText, Maximize, Minimize, Upload, X, Shapes, Keyboard, AlertTriangle, Share2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import ProcessingScreen from "@/components/ProcessingScreen";
import VideoPlayer from "@/components/VideoPlayer";
import TranscriptPanel from "@/components/TranscriptPanel";
import ParagraphTranscript from "@/components/ParagraphTranscript";
import AnimationModal from "@/components/AnimationModal";
import ExportModal from "@/components/ExportModal";
import PhraseRenderWorkspace from "@/components/PhraseRenderWorkspace";
import LanguageModal from "@/components/LanguageModal";
import CaptionTimeline from "@/components/CaptionTimeline";
import EditorRibbon from "@/components/EditorRibbon";
import { BackgroundPanel, VideoShapePanel } from "@/components/BackgroundShapePanels";
import { backgroundObject } from "@/lib/shapes";
import { ELEMENT_TYPES } from "@/lib/elements";
import SrtModal from "@/components/SrtModal";
import UsageMeter from "@/components/UsageMeter";
import WordStyleDialog from "@/components/WordStyleDialog";
import WindowSearch from "@/components/WindowSearch";
import ApplyScopeDialog, { askApplyScope } from "@/components/ApplyScopeDialog";
import { baseStyle, STYLE_FIELDS } from "@/lib/style";
import { sentenceEndsFromSegments } from "@/lib/presets";
import { loadBeatMap, setBeatClips, clearBeatMap } from "@/lib/beats";
import { setMotionTracks } from "@/lib/motion";
import { trackRegion } from "@/lib/tracker";
import { resolveActiveVideoClip, clipSourceUrl } from "@/lib/timeline";
import { baseClips, isEdited, resyncCaptionLayers, mapSpan, tlToSrc } from "@/lib/syncEdit";
import { syncCaptionsFromTranscript, layersEqual } from "@/lib/captionSync";
import { loadCustomFonts, ensureScriptFonts, loadLibraryFonts } from "@/lib/fonts";
import { isFeatureEnabled } from "@/lib/features";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, DEFAULT_CONFIG } from "@/lib/api";
import { getLocalAsset, clearLocalAsset } from "@/lib/localAssets";
import { fmtTime } from "@/lib/format";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChatWidget } from "@/components/chat/ChatWidget";

// Batch 3 — default media tracks: one video clip + its linked embedded-audio clip.
const defaultMediaTracks = (video) => {
  const dur = Math.max(0, video?.duration || 0);
  const vId = "clip-video-1", aId = "clip-audio-1";
  return [
    { id: "track-video", type: "video", name: "Video", locked: false, hidden: false, objects: [
      { id: vId, kind: "video", start: 0, end: dur, source_start: 0, source_end: dur, linked_id: aId, muted: false } ] },
    { id: "track-audio", type: "audio", name: "Audio", locked: false, hidden: false, objects: [
      { id: aId, kind: "audio", start: 0, end: dur, source_start: 0, source_end: dur, linked_id: vId, muted: false, volume: 1 } ] },
  ];
};

// Floating detail panel — anchored overlay that never permanently consumes the workspace.
// Closes on click-outside and Esc; the preview + timeline keep all their space underneath.
function FloatingPanel({ title, side = "right", wide = false, onClose, children, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => { if (e.target.closest?.('[role="dialog"], [data-radix-popper-content-wrapper]')) return; if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    window.addEventListener("keydown", onKey);
    // defer click-outside binding so the opening click doesn't immediately close it
    const t = setTimeout(() => window.addEventListener("mousedown", onDown), 0);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onDown); clearTimeout(t); };
  }, [onClose]);
  const pos = side === "left" ? "md:left-2" : "md:right-2";
  // Phones: bottom sheet across the full width; desktop: floating card.
  return (
    <div ref={ref} data-testid={testid}
      className={`fixed inset-x-0 bottom-0 z-[55] flex max-h-[70vh] flex-col overflow-hidden rounded-t-2xl border border-white/12 bg-[#121216]/97 shadow-2xl backdrop-blur md:absolute md:inset-x-auto md:bottom-auto md:top-2 md:max-h-[calc(100%-1rem)] md:rounded-xl ${pos} ${wide ? "md:w-[520px] md:max-w-[92vw]" : "md:w-[340px] md:max-w-[92vw]"} cm-pop-in`}>
      <div data-ws-pin="" className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
        <span className="min-w-0 truncate font-display text-[12px] font-bold uppercase tracking-wider text-white">{title}</span>
        <WindowSearch scope={ref} inline minControls={4} className="ml-auto mr-1 min-w-0" />
        <button data-testid="floating-panel-close" onClick={onClose} className="shrink-0 rounded-md p-1 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><X size={15} /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}


export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [name, setName] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeWordId, setActiveWordId] = useState(null);
  const [selectedWordId, setSelectedWordId] = useState(null);
  const [previewMode, setPreviewMode] = useState("combined");
  const [undo, setUndo] = useState([]);
  const [kbdHelp, setKbdHelp] = useState(false);
  useEffect(() => {
    if (!kbdHelp) return undefined;
    const k = (e) => { if (e.key === "Escape") setKbdHelp(false); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [kbdHelp]);
  const [redo, setRedo] = useState([]);
  const [animOpen, setAnimOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [v2Open, setV2Open] = useState(false);
  const activeEngineRef = useRef("V1");
  const v2AllowedRef = useRef(false);
  useEffect(() => {
    api.activeExportEngine().then((r) => { activeEngineRef.current = r.active; }).catch(() => {});
    api.featureAccess("export_v2").then((r) => { v2AllowedRef.current = !!r.allow || r.upgrade_required; }).catch(() => {});
  }, []);
  const openExportRoute = () => ((activeEngineRef.current === "V2" && v2AllowedRef.current) ? setV2Open(true) : setExportOpen(true));
  const [quickExport, setQuickExport] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [srtOpen, setSrtOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [styleWordOpen, setStyleWordOpen] = useState(false);
  const [styleWordText, setStyleWordText] = useState("");
  const [trMode, setTrMode] = useState("generate");
  const [scriptText, setScriptText] = useState("");
  const [showScriptPanel, setShowScriptPanel] = useState(false);
  const autoCaptionRef = useRef(false);
  const [trackSel, setTrackSel] = useState(null); // { layerId, objId, busy, progress }
  const [cropEditSel, setCropEditSel] = useState(null); // { trackId, objId } while dragging crop handles
  const createCaptionsRef = useRef(null);
  const [copiedStyle, setCopiedStyle] = useState(null);
  const [variant, setVariant] = useState(null);
  const [trial, setTrial] = useState(null);
  const [textVersions, setTextVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState("original");
  const [renderState, setRenderState] = useState(null);
  const [captionLayers, setCaptionLayers] = useState([]);
  const [mediaTracks, setMediaTracks] = useState([]);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [stage, setStage] = useState(null); // paragraph | synced | captions
  const [syncing, setSyncing] = useState(false);
  const [syncStage, setSyncStage] = useState("");
  const [syncedText, setSyncedText] = useState(null);
  const [retranscribing, setRetranscribing] = useState(false);
  const [capSel, setCapSel] = useState(null); // { layerId, ids:[objId] } — shared caption selection (timeline + preview)
  const [selectionKind, setSelectionKind] = useState(null); // caption|image|video|audio|element|null (drives ribbon context)
  const [activePopup, setActivePopup] = useState(null); // null|'media'|'details'|'presets'|'elements' — floating panels (one at a time)
  const isMobile = useIsMobile();
  const tlActions = useRef({}); // registered high-level timeline actions (driven by the ribbon)
  const [tlHeight, setTlHeight] = useState(240);
  const [tlCollapsed, setTlCollapsed] = useState(false);
  const [tlExpanded, setTlExpanded] = useState(false); // Batch 4 — timeline maximized (hides transcript)
  const [mediaColEl, setMediaColEl] = useState(null); // Project Media popup portal target
  const [inspectorColEl, setInspectorColEl] = useState(null); // Properties popup portal target
  const [presetRailEl, setPresetRailEl] = useState(null); // Caption Presets popup portal target
  const [previewFocus, setPreviewFocus] = useState(false);
  const curTimeRef = useRef(0);
  useEffect(() => {
    if (!previewFocus) return;
    const onEsc = (e) => { if (e.key === "Escape") setPreviewFocus(false); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [previewFocus]);

  // ── Continuous Background Rendering ─────────────────────────────────────────
  // While the user edits, render the export in the background so the Export click is a fast
  // finalize (reuses frame/segment/base caches; only dirtied segments re-encode). A debounced
  // idle trigger prepares the CURRENT timeline; a light poll drives the header status HUD.
  const bgOn = isFeatureEnabled("continuous_background_rendering");
  const bgAutoPrepareOn = isFeatureEnabled("background_auto_prepare");
  const bgHudOn = isFeatureEnabled("render_status_hud");
  const bgHashRef = useRef(null);
  // Export settings the Export modal sends by default — MUST match so the prepared render is reused.
  const bgSettings = { width: 1080, height: 1920, fps: 30, quality: "High" };
  // Stable content signature — recomputed each render but returns the SAME string unless the
  // timeline actually changed, so the debounced effect below is immune to incidental re-renders.
  const bgSig = useMemo(() => {
    try { return JSON.stringify({ c: config, cl: captionLayers, mt: mediaTracks, n: segments?.length || 0 }); }
    catch { return ""; }
  }, [config, captionLayers, mediaTracks, segments]);

  useEffect(() => {
    if (!bgOn || !bgHudOn) return;
    const hasContent = (segments?.length || 0) > 0 || (captionLayers || []).some((l) => (l.objects || []).length);
    if (!hasContent) { setRenderState(null); return; }
    // Content changed → the previously prepared render is now stale.
    bgHashRef.current = null;
    setRenderState((r) => ({ ...(r || {}), state: "rendering", ready: false, message: "Prepping your export…", progress: 0 }));
    if (!bgAutoPrepareOn) return;
    // Debounced idle trigger — only cleared when the signature changes (a real edit) or unmount.
    const t = setTimeout(async () => {
      try {
        const res = await api.backgroundRender(id, {
          config, settings: bgSettings, caption_layers: captionLayers, media_tracks: mediaTracks,
          include_webm: false, prepare: true,
        });
        bgHashRef.current = res?.hash || null;
        setRenderState(res);
      } catch { /* non-fatal — export still works from scratch */ }
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgOn, bgHudOn, bgAutoPrepareOn, id, bgSig]);

  useEffect(() => {
    if (!bgOn || !bgHudOn) return;
    let stop = false, timer;
    const tick = async () => {
      if (stop) return;
      const h = bgHashRef.current;
      if (h) {
        try {
          const s = await api.renderStatus(id, h);
          if (!stop && s?.hash === bgHashRef.current) setRenderState(s);
        } catch { /* ignore transient poll errors */ }
      }
      if (!stop) timer = setTimeout(tick, 3000);
    };
    timer = setTimeout(tick, 3000);
    return () => { stop = true; clearTimeout(timer); };
  }, [bgOn, bgHudOn, id]);

  const [transcriptOpen, setTranscriptOpen] = useState(false); // NLE → Transcript workspace overlay
  const [transcriptDirty, setTranscriptDirty] = useState(false);
  const syncBaselineRef = useRef("");
  const hasCaptions = captionLayers.some((l) => (l.objects || []).length > 0);
  const hasTranscript = segments.length > 0;
  const [transcribeLang, setTranscribeLang] = useState("auto");
  const [transcribeEngine, setTranscribeEngine] = useState("");
  const [hasOriginal, setHasOriginal] = useState(false);
  const srtInputRef = useRef(null);
  const { user } = useAuth() || {};
  const isPro = user && (user.role === "admin" || ["active", "authenticated"].includes(user.subscription_status));
  const [saving, setSaving] = useState(false);
  const [hasAnimation, setHasAnimation] = useState(false);
  const activeRef = useRef(null);
  const pollRef = useRef(null);
  const [localAsset] = useState(() => getLocalAsset(id));
  const [uploadState, setUploadState] = useState(localAsset ? "pending" : "idle"); // pending|uploading|finalizing|processing|ready|error|cancelled
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadModal, setUploadModal] = useState(null); // 'start' | 'success' | null
  const uploadBusy = ["pending", "uploading", "finalizing"].includes(uploadState);
  // Transcribe only once the video is truly on the server (never on a local blob mid-upload).
  const canTranscribe = !uploadBusy && uploadState !== "error";
  const uploadAbortRef = useRef(false);
  const uploadStartedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const p = await api.getProject(id);
      setProject(p);
      setName(p.name);
      if (p.transcript?.segments) setSegments(p.transcript.segments);
      if (p.animation?.config) { setConfig({ ...DEFAULT_CONFIG, ...p.animation.config }); setHasAnimation(true); }
      setVariant(p.transcript_variant || null);
      setTextVersions(p.text_versions || []);
      setActiveVersionId(p.active_version_id || "original");
      setHasOriginal(!!p.original_transcript);
      const layers = p.caption_layers || [];
      setCaptionLayers(layers);
      // Defensive repair: drop any timeline clip whose asset_id no longer resolves to an
      // existing media asset (orphans from older projects / deleted assets). Base clips have
      // no asset_id and are always kept.
      const assets = p.media_assets || [];
      const assetIds = new Set(assets.map((a) => a.id));
      const rawTracks = p.media_tracks?.length ? p.media_tracks : defaultMediaTracks(p.video);
      let orphans = 0;
      const repaired = rawTracks.map((t) => ({
        ...t,
        objects: (t.objects || []).filter((o) => { const ok = !o.asset_id || assetIds.has(o.asset_id); if (!ok) orphans += 1; return ok; }),
      }));
      setMediaTracks(repaired);
      setMediaAssets(assets);
      if (orphans > 0) toast.message(`Cleaned up ${orphans} timeline clip${orphans > 1 ? "s" : ""} referencing deleted media`);
      // NLE is now the home surface: land in the timeline editor immediately once a video
      // exists — transcription/captions are optional and triggered manually.
      setStage("captions");
      syncBaselineRef.current = (p.transcript?.full_text || "").trim();
      if (p.edit_stage === "synced") setSyncedText((p.transcript?.full_text || "").trim());
      setLoading(false);
      if (p.language_warning) toast.warning(p.language_warning, { duration: 8000 });
      return p;
    } catch { toast.error("Project not found"); navigate("/"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // poll while transcribing
  useEffect(() => {
    if (project && project.transcription?.status === "processing") {
      pollRef.current = setInterval(async () => {
        const p = await api.getProject(id);
        setProject(p);
        if (p.transcription?.status !== "processing") {
          clearInterval(pollRef.current);
          await load();
          if (p.video?.storage_path) setUploadState("ready");
          if (autoCaptionRef.current && p.transcription?.status === "ready") { autoCaptionRef.current = false; await createCaptionsRef.current?.(); setTranscriptOpen(false); }
          if (p.transcription?.status === "error") autoCaptionRef.current = false;
        }
      }, 1500);
      return () => clearInterval(pollRef.current);
    }
  }, [project?.transcription?.status, id]);

  // Poll for background readiness (remote original + editing proxy) so the preview
  // upgrades from the local blob → proxy automatically, without a manual refresh.
  useEffect(() => {
    const v = project?.video;
    const needsPoll = !!v && (v.pending || v.proxy_status === "generating" || (uploadState === "ready" && !v.storage_path));
    if (!needsPoll) return;
    const t = setInterval(async () => {
      try {
        const p = await api.getProject(id);
        setProject(p);
        const pv = p?.video;
        if (pv && !pv.pending && pv.proxy_status !== "generating") clearInterval(t);
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [project?.video?.pending, project?.video?.proxy_status, uploadState, id]);

  // Awkward-upload friendly note: when Phase C decides a source needs normalization
  // (heavy/problematic media_class → optimized media generating), reassure the user once.
  const awkwardToastRef = useRef("");
  useEffect(() => {
    const v = project?.video;
    if (!v) return;
    const optimizing = v.optimized_status === "generating" && ["heavy", "problematic"].includes(v.media_class);
    const key = `${id}:${v.source_hash || v.storage_path || ""}`;
    if (optimizing && awkwardToastRef.current !== key) {
      awkwardToastRef.current = key;
      toast.info("Optimizing your clip for smooth editing…", {
        description: "This one-time step makes scrubbing and export faster. You can keep editing meanwhile.",
        duration: 6000,
      });
    }
  }, [project?.video?.optimized_status, project?.video?.media_class, id]);

  // Local-first: chunked background upload of the locally-selected file (survives navigation).
  const runUpload = useCallback(async () => {
    const asset = getLocalAsset(id);
    const file = asset?.file;
    if (!file) { setUploadState("error"); return; }
    uploadAbortRef.current = false;
    setUploadState("uploading"); setUploadPct(0); setUploadModal("start");
    const CHUNK = 6 * 1024 * 1024;
    const CONCURRENCY = 4;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    let completed = 0;
    let nextIndex = 0;
    try {
      // Concurrent chunk workers — each chunk is an independent request with its
      // own retry, so 3–5 upload in parallel and any one can be retried in place.
      const uploadOne = async (i) => {
        const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
        let attempt = 0;
        while (true) {
          if (uploadAbortRef.current) throw new Error("aborted");
          try { await api.uploadChunk(id, i, total, blob); break; }
          catch (e) { attempt++; if (attempt >= 5) throw e; await sleep(400 * 2 ** attempt); }
        }
        completed++; setUploadPct(Math.round((completed / total) * 100));
      };
      const worker = async () => {
        while (true) {
          if (uploadAbortRef.current) return;
          const i = nextIndex++;
          if (i >= total) return;
          await uploadOne(i);
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, () => worker()));
      if (uploadAbortRef.current) return;
      setUploadState("finalizing");
      await api.uploadFinalize(id, total);
      setUploadState("ready");
      setUploadModal("success");
    } catch (e) {
      uploadAbortRef.current = true; // stop any sibling workers still in flight
      if (String(e?.message) === "aborted") return;
      setUploadState("error"); toast.error("Upload failed. Please try again.");
    }
  }, [id]);

  // Runs exactly once per project (ref-guarded so it isn't restarted/cancelled by StrictMode
  // double-invoke or by its own setUploadState calls).
  useEffect(() => {
    if (!localAsset || uploadStartedRef.current) return;
    uploadStartedRef.current = true;
    runUpload();
  }, [localAsset, id, runUpload]);

  const cancelUpload = useCallback(async () => {
    uploadAbortRef.current = true;
    setUploadState("cancelled");
    setUploadModal(null);
    try { await api.uploadCancel(id); } catch { /* noop */ }
    clearLocalAsset(id);
    navigate("/");
  }, [id, navigate]);

  const retryUpload = useCallback(() => {
    // File object is still in the local asset store — retry without re-selecting.
    runUpload();
  }, [runUpload]);


  // Revoke the local blob when leaving the editor.
  useEffect(() => () => clearLocalAsset(id), [id]);

  const flatWords = useMemo(() => segments.flatMap((s) => s.words || []), [segments]);
  const sentenceEndIds = useMemo(() => sentenceEndsFromSegments(segments), [segments]);

  // True timeline length = max end across ALL media tracks + caption objects (mirrors the
  // backend export duration). Falls back to the source video duration for legacy projects.
  const timelineDuration = useMemo(() => {
    let d = 0, hasMedia = false;
    (mediaTracks || []).forEach((tr) => (tr.objects || []).forEach((o) => { hasMedia = true; d = Math.max(d, o.end || 0); }));
    (captionLayers || []).forEach((l) => (l.objects || []).forEach((o) => { d = Math.max(d, o.end || 0); }));
    if (!hasMedia) d = Math.max(d, project?.video?.duration || 0);
    return d > 0 ? d : (project?.video?.duration || 0);
  }, [mediaTracks, captionLayers, project?.video?.duration]);

  const onTime = useCallback((t) => {
    setCurrentTime(t);
    curTimeRef.current = t;
    const w = flatWords.find((x) => t >= x.start - 0.02 && t <= x.end + 0.02);
    const wid = w?.word_id || null;
    if (wid !== activeRef.current) { activeRef.current = wid; setActiveWordId(wid); }
  }, [flatWords]);

  // Restore the playhead after a workspace rearrangement remounts the player (view-only change).
  useEffect(() => {
    if (stage !== "captions") return;
    const t = setTimeout(() => { if (curTimeRef.current > 0.05) playerRef.current?.seekTo(curTimeRef.current); }, 140);
    return () => clearTimeout(t);
  }, [previewFocus, stage]);

  const applyChange = (next) => { setUndo((u) => [...u.slice(-40), segments]); setRedo([]); setSegments(next); };  const doUndo = useCallback(() => { setUndo((u) => { if (!u.length) return u; const prev = u[u.length - 1]; setRedo((r) => [...r, segments]); setSegments(prev); return u.slice(0, -1); }); }, [segments]);
  const doRedo = useCallback(() => { setRedo((r) => { if (!r.length) return r; const nxt = r[r.length - 1]; setUndo((u) => [...u, segments]); setSegments(nxt); return r.slice(0, -1); }); }, [segments]);

  // Two-way sync: when the transcript changes, propagate to caption objects
  // (text/timing) via shared source word IDs — without inventing spoken words.
  useEffect(() => {
    if (!segments.length) return;
    setCaptionLayers((prev) => {
      if (!prev.some((l) => (l.objects || []).length)) return prev;
      // Transcript times are source-video times; on an edited timeline map them onto the kept clips.
      const clips = baseClips(mediaTracksRef.current);
      const toTl = isEdited(clips, project?.video?.duration || 0) ? (st, en) => mapSpan(st, en, clips) : null;
      const synced = syncCaptionsFromTranscript(segments, prev, toTl);
      return layersEqual(synced, prev) ? prev : synced;
    });
  }, [segments]);

  // Persist the caption timeline (layer order, splits, timings, styles) to the DB
  // so it survives refresh/reopen. Debounced; skipped for shared demo projects.
  const captionsLoadedRef = useRef(false);
  useEffect(() => { loadCustomFonts(); ensureScriptFonts(); loadLibraryFonts(); }, []);
  useEffect(() => {
    if (!project || project.is_demo) return;
    if (!captionsLoadedRef.current) { captionsLoadedRef.current = true; return; }
    const t = setTimeout(() => { api.updateProject(id, { caption_layers: captionLayers, media_tracks: mediaTracks, media_assets: mediaAssets }).catch(() => {}); }, 900);
    return () => clearTimeout(t);
  }, [captionLayers, mediaTracks, mediaAssets, project?.is_demo, id]);

  const seek = (t) => playerRef.current?.seekTo(t);

  // Caption selection + direct positioning from the preview (drag / X-Y).
  const selectCaption = useCallback((layerId, objId) => {
    setCapSel(objId ? { layerId, ids: [objId] } : null);
  }, []);
  const moveCaption = useCallback((layerId, objId, patch) => {
    setCaptionLayers((prev) => prev.map((l) => l.id !== layerId ? l : { ...l, objects: (l.objects || []).map((o) => o.id === objId ? { ...o, ...patch } : o) }));
  }, []);
  const selectedObjId = capSel && capSel.ids && capSel.ids.length === 1 ? capSel.ids[0] : null;

  // ==================== Centralized editor keyboard shortcut system ====================
  // ONE window keydown handler. Operates on the canonical selection (captions via capSel,
  // media via the timeline's own selection exposed through tlActions). Never hijacks keys
  // while the user is typing in a form control or a modal is open.
  const capSelRef = useRef(capSel);
  capSelRef.current = capSel;
  const kbRef = useRef({});
  const isTypingTarget = (el) => {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    const role = el.getAttribute && el.getAttribute("role");
    if (role === "slider" || role === "textbox" || role === "combobox" || role === "listbox" || role === "spinbutton" || role === "menuitem") return true;
    if (el.closest && el.closest('[role="slider"],[role="listbox"],[contenteditable="true"]')) return true;
    return false;
  };
  useEffect(() => {
    const onKey = (e) => {
      const undoKey = (e.metaKey || e.ctrlKey) && ["z", "y"].includes(e.key.toLowerCase());
      const onSlider = document.activeElement?.getAttribute?.("role") === "slider";
      if ((isTypingTarget(document.activeElement) || isTypingTarget(e.target)) && !(undoKey && onSlider)) return;
      // A modal/dialog owns the keyboard (its own ESC/controls) — don't run editor shortcuts.
      if (document.querySelector('[role="dialog"]')) return;
      const tl = tlActions.current;
      const ctx = kbRef.current;
      const hasSel = tl?.hasSelection?.();
      const mod = e.metaKey || e.ctrlKey, key = e.key.toLowerCase();
      const fstep = 1 / (ctx.fps || 30);
      if (e.code === "Space") {
        e.preventDefault();
        playerRef.current?.toggle?.();
      } else if (!mod && key === "k") { playerRef.current?.pause?.(); }
      else if (!mod && key === "l") { playerRef.current?.play?.(); }
      else if (!mod && key === "j") { ctx.seek?.(Math.max(0, ctx.currentTime - 5)); }
      else if (!mod && (e.key === "," || e.key === ".")) { e.preventDefault(); playerRef.current?.pause?.(); ctx.seek?.(Math.max(0, ctx.currentTime + (e.key === "," ? -fstep : fstep))); }
      else if (!mod && key === "s") { e.preventDefault(); tl?.split?.(); }
      else if (mod && key === "d") { e.preventDefault(); tl?.duplicate?.(); }
      else if (mod && e.shiftKey && key === "c") { e.preventDefault(); tl?.copyStyle?.(); }
      else if (mod && e.shiftKey && key === "v") { e.preventDefault(); tl?.pasteStyle?.(); }
      else if (mod && key === "c") { if (window.getSelection()?.toString()) return; if (tl?.copy?.()) e.preventDefault(); }
      else if (mod && key === "v") { e.preventDefault(); tl?.paste?.(); }
      else if (mod && key === "a") { e.preventDefault(); tl?.selectAll?.(); }
      else if (e.key === "Escape") { tl?.deselect?.(); }
      else if (!mod && (e.key === "+" || e.key === "=")) { e.preventDefault(); tl?.zoomIn?.(); }
      else if (!mod && (e.key === "-" || e.key === "_")) { e.preventDefault(); tl?.zoomOut?.(); }
      else if (e.key === "?") { setKbdHelp(true); }
      else if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) { if (!hasSel) return; e.preventDefault(); tl?.nudge?.(e.key === "ArrowLeft" ? -1 : 1); }
      else if (e.shiftKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) { e.preventDefault(); ctx.seek?.(Math.max(0, ctx.currentTime + (e.key === "ArrowLeft" ? -5 : 5))); }
      else if (e.key === "ArrowUp") {
        if (!hasSel) return;
        e.preventDefault(); tl.moveLayerUp?.();
      } else if (e.key === "ArrowDown") {
        if (!hasSel) return;
        e.preventDefault(); tl.moveLayerDown?.();
      } else if (e.key === "ArrowRight") {
        if (hasSel) { e.preventDefault(); tl.selectNext?.(); }
        else { const w = (ctx.flatWords || []).find((x) => x.start > ctx.currentTime + 0.01); if (w) ctx.seek?.(w.start); }
      } else if (e.key === "ArrowLeft") {
        if (hasSel) { e.preventDefault(); tl.selectPrev?.(); }
        else { const p = [...(ctx.flatWords || [])].reverse().find((x) => x.start < ctx.currentTime - 0.05); if (p) ctx.seek?.(p.start); }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (!hasSel) return;
        e.preventDefault(); tl.deleteSelection?.();
      } else if (e.key === "Home") { e.preventDefault(); ctx.seek?.(0); }
      else if (e.key === "End") { e.preventDefault(); ctx.seek?.(ctx.duration || 0); }
      else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); if (tl?.canUndo?.()) tl.undo(); else ctx.doUndo?.(); }
      else if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); if (tl?.canRedo?.()) tl.redo(); else ctx.doRedo?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { api.trialStatus().then(setTrial).catch(() => {}); }, []);
  const beatsWanted = useMemo(() => !!(config?.fx?.type && config.fx.type !== "none") || captionLayers.some((l) => (l.objects || []).some((o) => o.fx && o.fx.type !== "none")), [config?.fx, captionLayers]);
  const beatsLoadedRef = useRef(null);
  useEffect(() => {
    if (!beatsWanted || !project?.video?.storage_path || beatsLoadedRef.current === project.video.storage_path) return;
    beatsLoadedRef.current = project.video.storage_path;
    loadBeatMap(id);
  }, [beatsWanted, project?.video?.storage_path, id]);
  useEffect(() => { setBeatClips(mediaTracks); setMotionTracks(mediaTracks); }, [mediaTracks]);
  // Object tracking: follow the drawn box from the caption's start to its end (~12 samples / second).
  const runTrack = async (box) => {
    const ts = trackSel; if (!ts) return;
    const o = (captionLayers.find((l) => l.id === ts.layerId)?.objects || []).find((x) => x.id === ts.objId);
    if (!o) { setTrackSel(null); return; }
    const clips = baseClips(mediaTracks);
    const times = [];
    for (let t = o.start || 0; t <= (o.end || 0) + 1e-3; t += 1 / 12) { const src = tlToSrc(t, clips); if (src != null) times.push([t, src]); }
    if (times.length < 2) { toast.error("This caption isn't over the video."); return; }
    const active = resolveActiveVideoClip(mediaTracks, o.start || 0);
    const url = active ? clipSourceUrl(active.o, videoUrl) : videoUrl;
    const cv = document.querySelector('[data-testid="overlay-canvas"]');
    setTrackSel({ ...ts, busy: true, progress: 0 });
    try {
      const pts = await trackRegion({ url, box, times, aspect: cv ? cv.width / cv.height : 9 / 16, fitMode,
        onProgress: (p) => setTrackSel((cur) => (cur ? { ...cur, progress: p } : cur)) });
      const ox = (o.posX ?? config.posX ?? 0.5) - (box.x + box.w / 2), oy = (o.posY ?? config.posY ?? 0.72) - (box.y + box.h / 2);
      setCaptionLayers((ls) => ls.map((l) => (l.id !== ts.layerId ? l : { ...l, objects: l.objects.map((x) => (x.id === ts.objId ? { ...x, track: { pts, ox, oy } } : x)) })));
      toast.success("Caption now follows the object");
    } catch (e) { toast.error(e?.message || "Tracking failed"); }
    setTrackSel(null);
  };
  useEffect(() => () => clearBeatMap(), []);


  // Phase 2 — Caption → Transcript sync. When a caption spelling is fixed in the NLE,
  // offer (never silently) to update the matching transcript word too.
  const updateTranscriptWords = useCallback((changes) => {
    const map = {};
    changes.forEach((c) => { if (c.word_id) map[c.word_id] = c.to; });
    const next = segments.map((s) => {
      let touched = false;
      const words = (s.words || []).map((w) => { if (map[w.word_id]) { touched = true; return { ...w, word: map[w.word_id] }; } return w; });
      return touched ? { ...s, words, text: words.map((w) => w.word).join(" ") } : s;
    });
    applyChange(next);
    // Keep project.transcript in sync so the Transcript workspace paragraph reflects the change.
    const fullText = next.map((s) => s.text).join(" ");
    setProject((p) => ({ ...p, transcript: { ...(p.transcript || {}), full_text: fullText, segments: next } }));
    syncBaselineRef.current = fullText.trim();
    setSyncedText(fullText.trim());
  }, [segments]);

  const onCaptionWordEdit = useCallback((changes) => {
    if (!changes || !changes.length) return;
    const desc = changes.map((c) => `"${c.from}" → "${c.to}"`).join(", ");
    toast(changes.length > 1 ? "Update transcript words too?" : "Update transcript word too?", {
      description: desc, duration: 10000,
      action: { label: "Yes", onClick: () => { updateTranscriptWords(changes); toast.success("Transcript updated to match the caption"); } },
      cancel: { label: "No", onClick: () => {} },
    });
  }, [updateTranscriptWords]);

  // Phase 3 — Synchronize with Audio: align the edited paragraph to real word timings.
  const synchronize = async (text) => {
    const fromNle = stage === "captions"; // re-syncing from the Transcript workspace inside the NLE
    setSyncing(true);
    const stages = ["Preparing audio", "Analysing transcript", "Aligning speech", "Generating word timings", "Validating alignment"];
    let si = 0; setSyncStage(stages[0]);
    const tick = setInterval(() => { si = Math.min(si + 1, stages.length - 1); setSyncStage(stages[si]); }, 450);
    try {
      const res = await api.synchronize(id, text);
      clearInterval(tick); setSyncStage("Synchronization complete");
      applyChange(res.segments || res.transcript?.segments || []);
      setProject((p) => ({ ...p, transcript: res.transcript, edit_stage: fromNle ? "captions" : "synced" }));
      setSyncedText((text || "").trim());
      syncBaselineRef.current = (text || "").trim();
      setTranscriptDirty(false);
      if (fromNle) {
        // Existing two-way merge effect updates caption text/timing while preserving styling.
        toast.success(`Word timings updated — captions synced (${res.aligned_words}/${res.total_words} words)`);
        setTranscriptOpen(false); // return to the NLE showing the updated captions
      } else {
        setStage("synced");
        toast.success(`Synchronized ${res.aligned_words}/${res.total_words} words to the audio`);
      }
      const flagged = (res.total_words || 0) - (res.aligned_words || 0);
      if (flagged > 0) toast.warning(`${flagged} edited/added word${flagged > 1 ? "s" : ""} could not be confidently aligned — review the dashed-underline words.`, { duration: 7000 });
    } catch (e) {
      clearInterval(tick);
      toast.error(e?.response?.data?.detail || "Synchronization couldn't be completed. Your transcript is safe — try again.");
    }
    setSyncing(false); setSyncStage("");
  };

  // Phase 5 — return to the editable paragraph to make further changes / re-synchronize.
  const editTranscriptAgain = () => {
    setStage("paragraph");
    api.updateProject(id, { edit_stage: "paragraph" }).catch(() => {});
  };

  // Phase 7 — Create Captions: build the caption layer from the synced word transcript.
  createCaptionsRef.current = () => createCaptionsNow();
  const mediaTracksRef = useRef(mediaTracks);
  mediaTracksRef.current = mediaTracks;
  const createCaptionsNow = async () => {
    try {
      // Keep the caption mode the user already had (e.g. phrase) when rebuilding after a re-sync.
      const prevMode = ((captionLayers.find((l) => l.type === "captions")?.name || "").match(/Captions \((\w+)\)/) || [])[1];
      const res = await api.createCaptions(id, prevMode || "word");
      const fresh = await api.getProject(id).catch(() => null);
      const segs = fresh?.transcript?.segments || segments;
      setCaptionLayers(isEdited(baseClips(mediaTracks), project.video?.duration || 0)
        ? resyncCaptionLayers(res.caption_layers || [], segs, mediaTracks).layers : (res.caption_layers || []));
      syncBaselineRef.current = (project.transcript?.full_text || "").trim();
      setStage("captions");
      toast.success("Captions created — opening the timeline editor");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create captions."); }
  };

  // Phase 41 — NLE is the home surface; it stays open even with no captions yet.

  const save = async () => {
    setSaving(true);
    try {
      await api.updateProject(id, {
        name,
        transcript: { language: project.transcript?.language || "english", full_text: segments.map((s) => s.text).join(" "), segments },
        animation: hasAnimation ? { config } : null,
        export_settings: project.export_settings,
        caption_layers: captionLayers,
      });
      toast.success("Project saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  // Ask where to apply: All → global default (existing behaviour); This / Selected → per-caption design.
  const applyAnimationScoped = async (cfg) => {
    const tl = tlActions.current, info = tl?.scopeInfo?.();
    if (!info || !info.total) { applyAnimation(cfg); toast.success("Animation applied to preview"); return; }
    const mode = await askApplyScope({ what: "this animation", ...info });
    if (!mode) return;
    const b = baseStyle(cfg), style = {};
    STYLE_FIELDS.forEach((k) => { if (k !== "animation" && b[k] != null) style[k] = b[k]; });
    if (mode === "all") applyAnimation(cfg); // also becomes the default for new captions
    tl.applyPresetScoped({ id: null, name: "Animation", definition: { style, animation: cfg.animation || "pop", ...(cfg.fx ? { fx: cfg.fx } : {}) } }, mode);
  };
  const applyAnimation = (cfg) => {
    // The Animation modal edits the GLOBAL default. But existing caption objects carry their
    // own `o.animation`, which the renderer (animator.js drawCaptionFrame + backend
    // animation_engine) reads FIRST — so a global animation change would otherwise be inert on
    // already-populated captions. When the animation actually changes, stamp it onto every
    // caption object that isn't pinned to an explicit preset (preset-attached captions keep
    // their intentional look). This makes "change animation → preview + export update" work.
    const animChanged = cfg.animation && cfg.animation !== config.animation;
    setConfig(cfg);
    if (animChanged) {
      setCaptionLayers((prev) => prev.map((l) => (l.type === "captions" || l.type === "text")
        ? { ...l, objects: (l.objects || []).map((o) => (o.preset ? o : { ...o, animation: cfg.animation })) }
        : l));
    }
    setHasAnimation(true);
    setPreviewMode("combined");
  };

  // NOTE: The editor no longer auto-renders in the background. The browser preview is a
  // canvas overlay over the original video (see VideoPlayer), so editing transcript/words/
  // captions/styles/animation/emoji never triggers a server-side render. Full rendering
  // happens ONLY when the user clicks Export (async job). This removes the biggest hidden
  // CPU cost and the "constantly rendering" feel.

  const applyLanguage = (result) => {
    const transcript = result.transcript || result;
    applyChange(transcript.segments || []);
    setVariant(result.variant || null);
    setHasOriginal(true);
    if (result.text_versions) setTextVersions(result.text_versions);
    if (result.active_version_id) setActiveVersionId(result.active_version_id);
    setProject((p) => ({ ...p, transcript, transcript_variant: result.variant || null, original_transcript: result.original_transcript || p.original_transcript, text_versions: result.text_versions || p.text_versions, active_version_id: result.active_version_id || p.active_version_id }));
    toast.info("Saved. Review and edit the captions, then export.");
  };

  const revertLanguage = async () => {
    try {
      const { transcript } = await api.resetTranscript(id);
      applyChange(transcript.segments || []);
      setVariant(null);
      setActiveVersionId("original");
      setProject((p) => ({ ...p, transcript, transcript_variant: null, active_version_id: "original" }));
      toast.success("Restored the original transcript");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore."); }
  };

  const downloadSrt = () => setSrtOpen(true);

  const importSrt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { transcript } = await api.uploadSrt(id, file);
      applyChange(transcript.segments || []);
      setProject((p) => ({ ...p, transcript }));
      toast.success("SRT imported — timings preserved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not import that SRT."); }
  };

  // keyboard shortcuts: handled by the centralized handler above. Keep the live-value ref fresh.
  kbRef.current = { seek, currentTime, flatWords, duration: timelineDuration || project?.video?.duration || 0, doUndo, doRedo, fps: project?.video?.fps || 30 };

  const retry = async (language) => { await api.retranscribe(id, language); load(); };
  // Manual transcription trigger — the editor never auto-transcribes. Runs Google STT in
  // the background; the existing poll effect refreshes the transcript when it completes.
  const resyncScript = async () => {
    const txt = (project?.lyrics_text || "").trim();
    if (!txt) { setTrMode("script"); setShowScriptPanel(true); return; }
    try {
      await api.transcribe(id, transcribeLang, undefined, transcribeEngine, txt);
      autoCaptionRef.current = true;
      setProject((p) => ({ ...p, transcription: { status: "processing", stage: "Re-syncing your script", progress: 15, error: null } }));
      toast.success("Re-syncing your script with the audio…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start syncing."); }
  };
  // Original script / lyrics: aligned to the real audio, then captions are created automatically.
  const startScriptSync = async () => {
    const txt = scriptText.trim();
    if (!txt) { toast.error("Paste your lyrics or script first"); return; }
    if (!canTranscribe) { toast.error("Please wait for the video upload to finish."); return; }
    try {
      await api.transcribe(id, transcribeLang, undefined, transcribeEngine, txt);
      autoCaptionRef.current = true;
      setShowScriptPanel(false);
      setProject((p) => ({ ...p, transcription: { status: "processing", stage: "Listening to the audio", progress: 15, error: null } }));
      toast.success("Syncing your script with the audio…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start syncing."); }
  };
  const startTranscription = async (language) => {
    if (!canTranscribe) { toast.error("Please wait for the video upload to finish before transcribing."); return; }
    try {
      await api.transcribe(id, language || transcribeLang, undefined, transcribeEngine);
      setProject((p) => ({ ...p, transcription: { status: "processing", stage: "Extracting audio", progress: 15, error: null } }));
      toast.success("Transcribing… your captions will be ready shortly.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start transcription."); }
  };
  const quickStyleWord = (wordId, patch) => {
    setConfig((c) => ({ ...c, wordStyles: { ...(c.wordStyles || {}), [wordId]: { ...((c.wordStyles || {})[wordId] || {}), ...patch } } }));
    setHasAnimation(true);
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  const transcribing = project.transcription?.status === "processing";
  const trError = project.transcription?.status === "error";
  const remotePath = project.video?.storage_path;
  const proxyReady = project.video?.proxy_status === "ready" && project.video?.proxy_path;
  const proxyGenerating = project.video?.proxy_status === "generating";
  // Preview source priority: editing proxy (fast) → local blob (fast, avoids the heavy
  // original) → original server file. Export always uses the ORIGINAL (server-side).
  const videoUrl = proxyReady
    ? mediaUrl(project.video.proxy_path)
    : (localAsset?.url || (remotePath ? mediaUrl(remotePath) : null));
  // Original-quality source URL — used for EXPORT (never the proxy).
  const originalVideoUrl = remotePath ? mediaUrl(remotePath) : (localAsset?.url || null);

  // Video-not-ready guard: only when there's neither a remote file nor a local blob to play
  // (e.g. a shell reopened after its background upload was interrupted). Otherwise we always
  // land straight in the NLE editor — transcription is optional and never blocks editing.
  if (!videoUrl) {
    const upLabel = { pending: "Preparing…", uploading: `Uploading ${uploadPct}%`, finalizing: "Finishing upload…", ready: "Ready", error: "Upload failed" }[uploadState] || "Preparing your video…";
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0A0A0A]" data-testid="video-preparing">
        <Navbar minimal />
        <Loader2 size={28} className="animate-spin text-[#00F0FF]" />
        <p className="text-sm font-semibold text-white">{upLabel}</p>
        {uploadState === "error" && <button onClick={() => navigate("/")} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white">Back to Projects</button>}
      </div>
    );
  }

  const es = project.export_settings || {};
  const canvasW = es.width || project.video.width || 1080;
  const canvasH = es.height || project.video.height || 1920;
  const aspect = canvasW / canvasH;
  const fitMode = es.fit_mode || "cover"; // existing projects (no fit_mode) preserve cover
  const letterbox = es.letterbox || "black";
  const ASPECTS = [["9:16", 1080, 1920], ["1:1", 1080, 1080], ["16:9", 1920, 1080], ["4:5", 1080, 1350]];
  const patchExport = async (patch) => {
    const next = { ...project, export_settings: { ...es, ...patch } };
    setProject(next);
    try { await api.updateProject(id, { export_settings: next.export_settings }); } catch { /* non-fatal */ }
  };
  const setAspect = (label, w, h) => patchExport({ width: w, height: h, aspect: label, fit_mode: es.fit_mode || "contain" });
  const aspectFeatureOn = isFeatureEnabled("aspect_ratio_header");

  const previewTabs = (
    <div className="no-scrollbar flex flex-row items-center gap-2 overflow-x-auto border-b border-white/10 px-2 py-1.5 sm:flex-col sm:items-stretch sm:overflow-visible sm:px-4 sm:py-2">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-0.5">
          {[{ k: "video", l: "Original", i: Film }, { k: "overlay", l: "Text Overlay", i: Layers }, { k: "combined", l: "Combined", i: Eye }].map((m) => (
            <button key={m.k} data-testid={`preview-${m.k}`} onClick={() => setPreviewMode(m.k)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${previewMode === m.k ? "bg-[#00F0FF] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
              <m.i size={13} /> <span className="hidden sm:inline">{m.l}</span>
            </button>
          ))}
        </div>
        <span className="hidden font-plex text-xs text-[#666] sm:block">{canvasW}×{canvasH} · {es.aspect || project.video.aspect}</span>
      </div>
      {aspectFeatureOn && (
        <div className="flex shrink-0 items-center gap-2 sm:flex-wrap" data-testid="aspect-ratio-header">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-[#A1A1AA]">Aspect</span>
          {ASPECTS.map(([label, w, h]) => (
            <button key={label} data-testid={`aspect-${label.replace(":", "x")}`} onClick={() => setAspect(label, w, h)}
              className={`rounded-md px-2.5 py-1 text-xs font-bold transition-colors ${es.aspect === label ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`}>{label}</button>
          ))}
          <span className="ml-2 h-4 w-px bg-white/10" />
          <button data-testid="fit-mode-contain" onClick={() => patchExport({ fit_mode: "contain" })}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${fitMode === "contain" ? "bg-white/15 text-white" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`} title="Fit whole frame (no crop)">Contain</button>
          <button data-testid="fit-mode-cover" onClick={() => patchExport({ fit_mode: "cover" })}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${fitMode === "cover" ? "bg-white/15 text-white" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`} title="Fill frame (may crop edges)">Cover</button>
          {fitMode === "contain" && (
            <select data-testid="letterbox-select" value={letterbox} onChange={(e) => patchExport({ letterbox: e.target.value })}
              className="rounded-md border border-white/15 bg-[#0A0A0A] px-2 py-1 text-xs text-white" title="Letterbox fill">
              <option value="black">Black bars</option>
              <option value="blur">Blurred video</option>
            </select>
          )}
        </div>
      )}
    </div>
  );

  const playerBlock = () => (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black/40 p-1 sm:p-3" data-testid="video-preview-container">
      <VideoPlayer ref={playerRef} videoUrl={videoUrl} videoMeta={project.video} segments={segments}
        config={config} captionLayers={captionLayers} selectedObjId={selectedObjId} onSelectCaption={selectCaption} onMoveCaption={moveCaption}
        mediaTracks={mediaTracks} duration={timelineDuration} canvasRatio={aspect} fitMode={fitMode} letterbox={letterbox}
        previewMode={(hasAnimation || hasCaptions) ? previewMode : "video"} onTime={onTime} onPlayStateChange={setIsPlaying}
        cropEdit={cropEditSel ? (mediaTracks.find((t) => t.id === cropEditSel.trackId)?.objects || []).find((o) => o.id === cropEditSel.objId) || null : null}
        onCropChange={(crop) => setMediaTracks((tracks) => tracks.map((t) => (t.id === cropEditSel?.trackId ? { ...t, objects: (t.objects || []).map((o) => (o.id === cropEditSel.objId ? { ...o, crop } : o)) } : t)))}
        onCropDone={() => setCropEditSel(null)}
        trackDraw={trackSel ? { busy: !!trackSel.busy, progress: trackSel.progress || 0, onStart: runTrack, onCancel: () => setTrackSel(null) } : null} />
    </div>
  );

  const emojiBlock = null;

  const createAnimBar = (
    <div className="shrink-0 border-t border-white/10 p-3">
      <button data-testid="create-animation-btn" onClick={() => setAnimOpen(true)}
        className="pulse-glow flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-3.5 text-base font-bold text-white transition-transform hover:scale-[1.01]">
        <Sparkles size={19} /> {hasAnimation ? "Edit Text Animation" : "Create Text Animation"}
      </button>
    </div>
  );

  const transcriptBlock = (
    <TranscriptPanel segments={segments} activeWordId={activeWordId} onSeek={seek}
      onChange={applyChange} selectedWordId={selectedWordId} setSelectedWordId={setSelectedWordId}
      onStyleWord={() => setStyleWordOpen(true)} styledWordIds={config.wordStyles || {}}
      onQuickStyle={quickStyleWord} projectId={id} showDelete={stage === "synced"} />
  );

  // Phase 21/23 — re-run transcription with an explicit language and/or keywords.
  const retranscribeWith = async ({ language, vocabulary } = {}) => {
    setRetranscribing(true);
    try {
      await api.retranscribe(id, language, vocabulary);
      toast.success("Re-transcribing… this will refresh your transcript.");
      const p = await api.getProject(id);
      setProject(p);
      setStage("paragraph");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-transcribe."); }
    setRetranscribing(false);
  };

  // AI Summary — verification-only. Uses the CURRENT edited transcript; never mutates transcript/captions.
  const summarize = async (text) => {
    const res = await api.transcriptSummary(id, text, project.transcript?.language);
    setProject((p) => ({ ...p, ai_summary: res.summary, ai_summary_source: text, ai_summary_transcript_hash: res.transcript_hash }));
    return res;
  };

  const paragraphBlock = (
    <ParagraphTranscript
      initialText={project.transcript?.full_text || segments.map((s) => s.text).join(" ")}
      language={project.transcript?.language} audioDuration={project.video?.duration}
      languageProbability={project.transcript?.language_probability}
      onSynchronize={synchronize} syncing={syncing} syncStage={syncStage} syncedText={syncedText}
      languageChoice={project.language_choice} speechIncomplete={project.speech_incomplete}
      onChooseLanguage={(code) => retranscribeWith({ language: code })}
      initialVocabulary={project.vocabulary} retranscribing={retranscribing}
      onApplyKeywords={(vocab) => retranscribeWith({ vocabulary: vocab })}
      onSummarize={summarize} initialSummary={project.ai_summary} initialSummarySource={project.ai_summary_source}
      onTextChange={(t) => { if (stage === "captions") setTranscriptDirty((t || "").trim() !== syncBaselineRef.current); }}
      transcriptMismatch={(stage === "captions" && transcriptDirty) || project.transcript_mismatch} coverage={project.coverage} />
  );

  // Transcribe panel — shown in the Transcript workspace when there's no transcript yet (or one
  // is being generated). Transcription is 100% manual; the editor never auto-transcribes.
  const TRANSCRIBE_LANGS = [["auto", "Auto detect"], ["english", "English"], ["telugu", "Telugu"], ["hindi", "Hindi"], ["tamil", "Tamil"], ["kannada", "Kannada"], ["malayalam", "Malayalam"], ["bengali", "Bengali"], ["marathi", "Marathi"]];
  const transcribePanel = (
    <div className="flex h-full flex-col overflow-y-auto p-6" data-testid="transcribe-panel">
      <div className="mx-auto w-full max-w-md space-y-5">
        <div>
          <div className="mb-4 flex rounded-xl border border-white/10 bg-white/[0.02] p-1" data-testid="transcribe-mode-toggle">
            {[["generate", "Generate transcript"], ["script", "Original script"]].map(([k, l]) => (
              <button key={k} onClick={() => setTrMode(k)} data-testid={`transcribe-mode-${k}`}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${trMode === k ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "text-[#A1A1AA] hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <h2 className="font-display text-xl font-black text-white">{trMode === "script" ? "Paste your lyrics or script" : "Generate transcript"}</h2>
          <p className="mt-1 text-sm text-[#A1A1AA]">{trMode === "script"
            ? "Paste the exact words (one line per caption line). We listen to the audio, lock each word to when it's sung or spoken, then create captions."
            : "Transcribe your video's speech to build synced captions, with automatic multilingual detection. This step is optional — you can keep editing without it."}</p>
        </div>
        {transcribing ? (
          <div className="rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.06] p-5" data-testid="transcribe-progress">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Loader2 size={16} className="animate-spin text-[#00F0FF]" /> {project.transcription?.stage || "Transcribing…"}</div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#00F0FF] transition-all" style={{ width: `${Math.min(100, project.transcription?.progress || 20)}%` }} /></div>
            <p className="mt-3 text-xs text-[#666]">You can close this panel and keep editing — captions will appear here when it's done.</p>
          </div>
        ) : (
          <>
            {trError && (
              <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="transcribe-error">{project.transcription?.error || "Transcription failed. Please try again."}</div>
            )}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA]">Spoken language</label>
              <select value={transcribeLang} onChange={(e) => setTranscribeLang(e.target.value)} data-testid="transcribe-language-select"
                className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white outline-none focus:border-[#00F0FF]/50">
                {TRANSCRIBE_LANGS.map(([v, l]) => <option key={v} value={v} className="bg-[#141414]">{l}</option>)}
              </select>
            </div>
            {trMode === "script" ? (<>
              <textarea value={scriptText} onChange={(e) => setScriptText(e.target.value)} rows={10} data-testid="transcribe-script-input"
                placeholder={"Line 1 of your lyrics\nLine 2 of your lyrics\n…"}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm leading-relaxed text-white outline-none placeholder:text-[#555] focus:border-[#00F0FF]/50" />
              <div className="flex justify-between text-[11px] text-[#666]"><span>{scriptText.split("\n").filter((l) => l.trim()).length} lines · {scriptText.split(/\s+/).filter(Boolean).length} words</span><span>Works with edited (trimmed) video too</span></div>
              <button onClick={startScriptSync} data-testid="sync-script-btn" disabled={!canTranscribe || !scriptText.trim()}
                className="pulse-glow flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#0891b2] py-3.5 text-base font-bold text-black transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100">
                <Wand2 size={19} /> Sync &amp; Create Captions
              </button>
            </>) : (
            <button onClick={() => startTranscription()} data-testid="generate-transcript-btn" disabled={!canTranscribe}
              className="pulse-glow flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-3.5 text-base font-bold text-white transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100">
              <FileText size={19} /> {uploadBusy ? `Uploading video… ${uploadPct}%` : "Generate Transcript"}
            </button>
            )}
            {showScriptPanel && <button onClick={() => setShowScriptPanel(false)} data-testid="script-panel-cancel" className="w-full text-center text-xs text-[#A1A1AA] hover:text-white">Cancel — keep current transcript</button>}
            {uploadBusy && <p className="text-center text-xs text-[#666]">Transcription unlocks once your video finishes uploading to the server.</p>}
          </>
        )}
      </div>
    </div>
  );

  const createCaptionsBar = (
    <div className="shrink-0 space-y-2 border-t border-white/10 p-3">
      <div className="flex items-center justify-between rounded-lg bg-[#00F0FF]/[0.06] px-3 py-2 text-xs" data-testid="sync-status-chip">
        <span className="flex items-center gap-1.5 font-semibold text-[#00F0FF]" data-testid="sync-status-chip-label"><Check size={14} /> Audio synchronized</span>
        <span className="flex items-center gap-3">
          <button data-testid="original-script-link" onClick={() => { setTrMode("script"); setScriptText((t) => t || (project.lyrics_text || "")); setShowScriptPanel(true); }} className="font-semibold text-[#00F0FF] underline-offset-2 hover:underline">Original script</button>
                  <button data-testid="edit-transcript-btn" onClick={editTranscriptAgain} className="text-[#A1A1AA] underline-offset-2 hover:text-white hover:underline">Edit transcript</button>
        </span>
      </div>
      <button data-testid="create-captions-btn" onClick={createCaptionsNow}
        className="pulse-glow flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-3.5 text-base font-bold text-white transition-transform hover:scale-[1.01]">
        <Layers size={19} /> Create Captions
      </button>
      <div className="text-center text-[11px] text-[#666]">Delete words with ✕, or edit the full transcript above</div>
    </div>
  );

  const captionTimelineBlock = (
    <CaptionTimeline projectId={id} isDemo={project.is_demo} duration={project.video.duration} captionConfig={config}
      onGenerateInstead={project.transcript_source === "lyrics" ? () => { if (window.confirm("Replace your pasted script with an automatic transcript of the audio?")) { startTranscription(); setTranscriptOpen(true); } } : null}
      onTrackStart={(ts) => { playerRef.current?.pause?.(); const o = (captionLayers.find((l) => l.id === ts.layerId)?.objects || []).find((x) => x.id === ts.objId); if (o) seek(o.start || 0); setTrackSel(ts); }}
      onCropEdit={(sel) => { playerRef.current?.pause?.(); setCropEditSel(sel); }} cropEditing={!!cropEditSel}
      transcriptSegments={segments} onTranscribeAgain={async () => {
        try {
          await api.transcribe(id, transcribeLang, undefined, transcribeEngine, project.lyrics_text || undefined);
          autoCaptionRef.current = true;
          setProject((p) => ({ ...p, transcription: { status: "processing", stage: "Listening to the edited audio", progress: 15, error: null } }));
          toast.success(project.lyrics_text ? "Re-syncing your lyrics with the edited audio…" : "Transcribing the edited video again…");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start."); }
      }}
      sentenceEndIds={sentenceEndIds} onStyleWord={(wid, text) => { setSelectedWordId(wid); setStyleWordText(text || ""); setStyleWordOpen(true); }}
      currentTime={currentTime} onSeek={seek} onPause={() => playerRef.current?.pause?.()} layers={captionLayers} onChange={setCaptionLayers}
      activeWordId={activeWordId} collapsed={tlCollapsed} onToggleCollapse={() => setTlCollapsed((v) => !v)}
      sel={capSel} setSel={setCapSel} fps={project.video?.fps || 30} isPlaying={isPlaying}
      mediaTracks={mediaTracks} onMediaChange={setMediaTracks} videoUrl={videoUrl}
      mediaAssets={mediaAssets} onAssetAdded={(a) => setMediaAssets((prev) => [...prev, a])} onAssetsChange={setMediaAssets}
      baseAsset={project.video ? { id: "base-video", kind: "video", name: project.video.filename || name || "main-video", duration: project.video.duration, url: project.video.storage_path, is_base: true } : null}
      onCaptionWordEdit={onCaptionWordEdit}
      nleLayout mediaPortalEl={mediaColEl} inspectorPortalEl={inspectorColEl} inspectorVisible={activePopup === "details"}
      presetPortalEl={presetRailEl} config={config} actionsRef={tlActions} onSelectionKind={setSelectionKind}
      onConfigPatch={(patch) => { setConfig((c) => ({ ...c, ...patch })); setHasAnimation(true); }}
      projectMeta={{ width: project.video.width, height: project.video.height, aspect: project.video.aspect }}
      expanded={tlExpanded} onToggleExpanded={() => setTlExpanded((v) => !v)} />
  );

  const startTlResize = (e) => {
    e.preventDefault();
    const startY = e.clientY; const h0 = tlHeight;
    const move = (ev) => setTlHeight(Math.max(150, Math.min(window.innerHeight * 0.6, h0 + (startY - ev.clientY))));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const previewNode = (
    <div className={previewFocus ? "fixed inset-0 z-[60] flex flex-col bg-black" : "flex h-full min-h-0 flex-col bg-black"} data-testid="nle-preview-column">
      {previewTabs}
      {playerBlock(true)}
      {previewFocus && (
        <button data-testid="preview-focus-exit" onClick={() => setPreviewFocus(false)}
          className="absolute right-4 top-4 z-10 flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"><Minimize size={14} /> Exit Focus <span className="text-[#A1A1AA]">Esc</span></button>
      )}
    </div>
  );
  const timelineNode = <div className="h-full min-h-0 overflow-hidden" data-testid="nle-timeline-dock">{captionTimelineBlock}</div>;

  const ribbonActions = {
    addText: () => tlActions.current?.addText?.(),
    addCaptionLayer: () => tlActions.current?.addCaptionLayer?.(),
    createCaptions: () => (hasCaptions ? tlActions.current?.addCaptionLayer?.() : setTranscriptOpen(true)),
    addElement: (t) => { tlActions.current?.addElement?.(t); setActivePopup(null); },
    transcribe: () => setTranscriptOpen(true),
    translate: () => setLangOpen(true),
    transliterate: () => setLangOpen(true),
    openAnimation: () => setAnimOpen(true),
    openExport: () => (openExportRoute()),
    playPause: () => playerRef.current?.toggle?.(),
    moveLayerUp: () => tlActions.current?.moveLayerUp?.(),
    moveLayerDown: () => tlActions.current?.moveLayerDown?.(),
    deleteSelection: () => tlActions.current?.deleteSelection?.(),
    openBackground: () => setActivePopup("background"),
    openShape: () => setActivePopup("shape"),
  };

  // Background Canvas lives as a dedicated 'background' track inside media_tracks (so it flows
  // through autosave, the render hash and the server compositor). It renders behind everything.
  const setBackground = (bg) => {
    setMediaTracks((prev) => {
      const rest = (prev || []).filter((t) => t.type !== "background");
      if (!bg) return rest;
      return [{ id: "track-background", type: "background", name: "Background", locked: false, hidden: false, objects: [{ id: "bg-1", ...bg }] }, ...rest];
    });
  };
  const selectedVideo = activePopup === "shape" ? tlActions.current?.getSelectedVideo?.() : null;
  const imageAssets = (mediaAssets || []).filter((a) => (a.media_type || a.kind || "").includes("image") || /\.(png|jpe?g|webp|gif)$/i.test(a.name || a.storage_url || ""));

  // CONTENT-FIRST workspace: Preview LEFT (~26%) · Timeline RIGHT (~74%), both full height.
  // No permanent side panels — details open as floating popups above this workspace.
  const renderWorkspace = () => (
    <ResizablePanelGroup direction={isMobile ? "vertical" : "horizontal"} autoSaveId={`cm-cf-${isMobile ? "v" : "h"}-${id}`} className="min-h-0 flex-1" data-testid="workspace-nle">
      <ResizablePanel id="preview" order={1} defaultSize={isMobile ? 50 : 26} minSize={isMobile ? 30 : 18} maxSize={isMobile ? 70 : 45}>
        <div className="h-full border-b border-white/10 md:border-b-0 md:border-r">{previewNode}</div>
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel id="timeline" order={2} defaultSize={74} minSize={45}>{timelineNode}</ResizablePanel>
    </ResizablePanelGroup>
  );

  // Floating popups — portal targets live inside; mounting/unmounting toggles the portals.
  const mediaPopup = activePopup === "media" && (
    <FloatingPanel testid="media-popup" title="Project Media" side="left" onClose={() => setActivePopup(null)}>
      <div ref={setMediaColEl} className="flex h-[68vh] flex-col overflow-hidden" data-testid="nle-media-column" />
    </FloatingPanel>
  );
  const detailsPopup = activePopup === "details" && (
    <FloatingPanel testid="details-popup" title="Properties" side="right" onClose={() => setActivePopup(null)}>
      <div ref={setInspectorColEl} className="flex max-h-[72vh] flex-col overflow-y-auto" data-testid="nle-inspector-column" />
    </FloatingPanel>
  );
  const presetsPopup = activePopup === "presets" && (
    <FloatingPanel testid="presets-popup" title="Caption Presets" side="right" wide onClose={() => setActivePopup(null)}>
      <div ref={setPresetRailEl} className="flex h-[70vh] flex-col overflow-hidden" data-testid="nle-presets-column" />
    </FloatingPanel>
  );
  const elementsPopup = activePopup === "elements" && (
    <FloatingPanel testid="elements-popup" title="Elements" side="left" onClose={() => setActivePopup(null)}>
      <div className="p-2">
        <p className="px-1 pb-2 text-[11px] text-[#888]">Click to add a shape to the canvas at the playhead.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {ELEMENT_TYPES.map((el) => (
            <button key={el.v} data-testid={`popup-element-${el.v}`} onClick={() => ribbonActions.addElement(el.v)}
              className="flex flex-col items-center gap-1.5 rounded-lg border border-white/10 bg-[#141418] px-2 py-3 text-[10px] font-semibold text-white transition-colors hover:border-[#EC4899]/60 hover:bg-[#1E1E24]">
              <Shapes size={18} className="text-[#EC4899]" /> {el.l}
            </button>
          ))}
        </div>
      </div>
    </FloatingPanel>
  );
  const backgroundPopup = activePopup === "background" && (
    <FloatingPanel testid="background-popup" title="Background Canvas" side="left" onClose={() => setActivePopup(null)}>
      <BackgroundPanel background={backgroundObject(mediaTracks)} onSetBackground={setBackground} imageAssets={imageAssets} />
    </FloatingPanel>
  );
  const shapePopup = activePopup === "shape" && (
    <FloatingPanel testid="shape-popup" title="Video Shape" side="right" onClose={() => setActivePopup(null)}>
      <VideoShapePanel clip={selectedVideo?.clip} onPatch={(p) => tlActions.current?.patchSelectedVideo?.(p)} />
    </FloatingPanel>
  );
  const floatingPopups = (<>{mediaPopup}{detailsPopup}{presetsPopup}{elementsPopup}{backgroundPopup}{shapePopup}
    {kbdHelp && (
      <div data-testid="kbd-help-overlay" onClick={() => setKbdHelp(false)} className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4">
        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#111] p-5 shadow-2xl">
          <WindowSearch />
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-lg font-black text-white">Keyboard shortcuts</h3>
            <button data-testid="kbd-help-close" onClick={() => setKbdHelp(false)} className="rounded-lg p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-white"><X size={16} /></button>
          </div>
          <div className="space-y-2 text-sm">
            {[["Space", "Play / Pause"], ["J / K / L", "Back 5s / Pause / Play"], [", / .", "Previous / next frame"], ["Shift + ← / →", "Jump 5 seconds"], ["↑", "Move layer up"], ["↓", "Move layer down"], ["←", "Previous caption / object"], ["→", "Next caption / object"], ["Alt + ← / →", "Move selection 1 second"], ["S", "Split at playhead"], ["⌘/Ctrl + D", "Duplicate"], ["⌘/Ctrl + C / V", "Copy / paste captions"], ["⌘/Ctrl + Shift + C / V", "Copy / paste style"], ["⌘/Ctrl + A", "Select all in layer"], ["Esc", "Deselect"], ["+ / −", "Zoom timeline"], ["Delete / Backspace", "Delete selected"], ["Home / End", "Jump to start / end"], ["⌘/Ctrl + Z", "Undo"], ["⌘/Ctrl + Y", "Redo"], ["?", "Show shortcuts"]].map(([k, d]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-[#A1A1AA]">{d}</span>
                <kbd className="rounded-md border border-white/15 bg-white/[0.04] px-2 py-0.5 font-plex text-xs text-white">{k}</kbd>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-[#666]">Shortcuts are disabled while you're typing in a text field, slider or dropdown.</p>
        </div>
      </div>
    )}
  </>);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#0A0A0A]">
      {/* Top bar */}
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-white/10 px-2 sm:px-4" data-testid="editor-topbar">
        <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-3">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><ArrowLeft size={16} /> <span className="hidden sm:inline">Projects</span></button>
          <div className="h-5 w-px bg-white/10" />
          <input data-testid="project-name" value={name} onChange={(e) => setName(e.target.value)} className="min-w-0 max-w-[110px] truncate sm:max-w-[240px] rounded-md bg-transparent px-2 py-1 font-display text-sm font-bold outline-none hover:bg-white/5 focus:bg-white/5" />
          {variant && (
            <span data-testid="variant-badge" className="hidden items-center gap-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-2.5 py-1 text-[11px] font-semibold text-[#00F0FF] sm:flex">
              {variant.mode === "translate" ? "Translated" : "Transliterated"} · {variant.target}
              <button data-testid="variant-revert-btn" onClick={revertLanguage} title="Restore original transcript" className="ml-1 hover:text-white"><RotateCcw size={12} /></button>
            </span>
          )}
          {trial?.is_trial && (
            <span data-testid="trial-badge" title={trial.expired ? "Trial ended" : `${trial.remaining_exports} exports left · up to ${trial.max_resolution}p · watermark ${trial.watermark ? "on" : "off"}`}
              className={`hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold sm:flex ${trial.expired || trial.remaining_exports <= 0 ? "border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444]" : "border-[#FFC53D]/40 bg-[#FFC53D]/10 text-[#FFC53D]"}`}>
              {trial.expired ? "Trial ended" : `Free trial · ${trial.remaining_exports} export${trial.remaining_exports === 1 ? "" : "s"} left`}
              <a href="/pricing" className="ml-0.5 underline hover:opacity-80">Upgrade</a>
            </span>
          )}
          {(uploadState === "uploading" || uploadState === "finalizing" || uploadState === "pending") && (
            <span data-testid="bg-upload-chip" className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA] md:flex">
              <Loader2 size={11} className="animate-spin text-[#00F0FF]" /> {uploadState === "finalizing" ? "Finishing upload…" : uploadState === "pending" ? "Preparing…" : `Uploading ${uploadPct}%`}
            </span>
          )}
          {!uploadBusy && proxyGenerating && (
            <span data-testid="proxy-status-chip" className="hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA] md:flex">
              <Loader2 size={11} className="animate-spin text-[#00F0FF]" /> Preparing editing preview…
            </span>
          )}
          {!uploadBusy && proxyReady && (
            <span data-testid="proxy-ready-chip" className="hidden items-center gap-1.5 rounded-full border border-[#00F0FF]/25 bg-[#00F0FF]/[0.06] px-2.5 py-1 text-[11px] font-semibold text-[#00F0FF] md:flex">
              <Check size={11} /> Editing preview ready
            </span>
          )}
        </div>
        <div className="no-scrollbar flex min-w-0 items-center gap-1.5 overflow-x-auto" data-testid="editor-topbar-actions">
          {stage === "captions" && (
            <div className="mr-1 flex items-center gap-0.5">
              <button data-testid="preview-focus-btn" onClick={() => setPreviewFocus(true)} title="Preview Focus (Esc to exit)"
                className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Maximize size={16} /></button>
            </div>
          )}
          {stage === "captions" && (
            <button data-testid="transcript-tab-btn" onClick={() => setTranscriptOpen((v) => !v)} disabled={!canTranscribe && !hasTranscript}
              title={!canTranscribe && !hasTranscript ? `Uploading video… ${uploadPct}%` : undefined}
              className={`mr-1 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${transcriptOpen ? "bg-[#00F0FF]/15 text-[#00F0FF]" : !hasTranscript ? "bg-[#8B5CF6]/20 text-white ring-1 ring-[#8B5CF6]/50 hover:bg-[#8B5CF6]/30" : "text-[#A1A1AA] hover:bg-white/10 hover:text-white"} ${transcriptDirty ? "ring-1 ring-[#FFC53D]/60" : ""}`}>
              {transcribing ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} <span className="hidden sm:inline">{uploadBusy && !hasTranscript ? `Uploading ${uploadPct}%` : transcribing ? "Transcribing…" : !hasTranscript ? "Transcribe" : "Transcript"}</span>
              {transcriptDirty && <span className="h-1.5 w-1.5 rounded-full bg-[#FFC53D]" title="Transcript changed" />}
            </button>
          )}
          <input type="file" accept=".srt" ref={srtInputRef} onChange={importSrt} className="hidden" data-testid="srt-file-input" />
          <button data-testid="undo-btn" onClick={doUndo} disabled={!undo.length} className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"><Undo2 size={16} /></button>
          <button data-testid="redo-btn" onClick={doRedo} disabled={!redo.length} className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"><Redo2 size={16} /></button>
          <button data-testid="srt-download-btn" onClick={downloadSrt} title="Export .srt" className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><FileDown size={16} /></button>
          <button data-testid="srt-import-btn" onClick={() => srtInputRef.current?.click()} title="Import .srt" className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><FileUp size={16} /></button>
          <button data-testid="kbd-help-btn" onClick={() => setKbdHelp(true)} title="Keyboard shortcuts" className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Keyboard size={16} /></button>
          <button data-testid="usage-btn" onClick={() => setUsageOpen(true)} title="Usage this month" className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Gauge size={16} /></button>
          <button data-testid="language-btn" onClick={() => setLangOpen(true)} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white"><Languages size={15} /> <span className="hidden sm:inline">Language</span></button>
          <button data-testid="save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#A1A1AA] transition-colors hover:bg-white/10 hover:text-white">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} <span className="hidden sm:inline">Save</span></button>
          <ChatWidget />
          <button data-testid="quick-share-btn" onClick={() => { setQuickExport(true); setExportOpen(true); }} title="One-tap export & share"
            className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold text-white md:hidden" style={{ background: "linear-gradient(90deg,#E1306C,#F77737)" }}><Share2 size={15} /> Share</button>
          <button data-testid="export-btn" onClick={() => (openExportRoute())} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${renderState?.ready ? "bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90" : "bg-white text-black hover:bg-white/90"}`}><Download size={15} /> Export</button>
        </div>
      </div>

      {renderState && segments.length > 0 && (
        <div data-testid="render-status" className="flex shrink-0 items-center gap-2 border-b border-white/10 bg-[#0d0d0d] px-4 py-1.5 text-xs">
          {renderState.ready
            ? <span className="flex items-center gap-1.5 font-semibold text-[#22c55e]" data-testid="render-status-ready"><span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Ready — your export will be instant{renderState.segments_cached > 0 ? <span className="hidden font-normal text-[#6b7280] sm:inline"> · {renderState.segments_cached}/{renderState.segments_total} segments reused from cache</span> : null}</span>
            : renderState.state === "rendering"
              ? <span className="flex items-center gap-1.5 font-semibold text-[#00F0FF]" data-testid="render-status-prepping"><Loader2 size={12} className="animate-spin" /> Prepping your export…{renderState.segments_total > 0 ? ` (${renderState.segments_done}/${renderState.segments_total} segments)` : (renderState.progress ? ` (${renderState.progress}%)` : "")} <span className="hidden font-normal text-[#6b7280] sm:inline">— so your final export is quick</span></span>
              : <span className="flex items-center gap-1.5 text-[#A1A1AA]" data-testid="render-status-idle"><span className="h-1.5 w-1.5 rounded-full bg-[#A1A1AA]" /> {renderState.message || "Changes detected"}</span>}
        </div>
      )}

      {/* Main */}
      {stage === "captions" && transcriptOpen ? (
        /* NLE → Transcript workspace (Editor stays mounted → all NLE edits preserved) */
        <div className="flex min-h-0 flex-1 flex-col" data-testid="nle-transcript-workspace">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-[#0d0d0d] px-3 py-2 sm:px-4">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white"><FileText size={15} className="text-[#00F0FF]" /> Transcript
              {transcriptDirty && <span data-testid="transcript-changed-banner" className="ml-2 rounded-full border border-[#FFC53D]/50 bg-[#FFC53D]/10 px-2.5 py-1 text-[11px] font-semibold text-[#FFC53D]">Transcript changed — click Synchronize with Audio to update captions</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasTranscript && !transcribing && !showScriptPanel && project.transcript_source === "lyrics" && (<>
                <button data-testid="resync-script-btn" onClick={() => { setScriptText(project.lyrics_text || ""); resyncScript(); }} title="Not happy with the timing? Listen to the audio and match your words again"
                  className="flex items-center gap-1.5 rounded-lg border border-[#8B5CF6]/50 px-3 py-1.5 text-sm font-bold text-[#c4b5fd] hover:bg-[#8B5CF6]/10"><RefreshCw size={14} /> Re-sync my script</button>
                <button data-testid="generate-instead-btn" onClick={() => { if (window.confirm("Replace your pasted script with an automatic transcript of the audio?")) startTranscription(); }}
                  className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-bold text-[#A1A1AA] hover:text-white"><FileText size={14} /> Generate transcript instead</button>
              </>)}
              {hasTranscript && !transcribing && !showScriptPanel && (
                <button data-testid="original-script-btn" onClick={() => { setTrMode("script"); setScriptText((t) => t || (project.lyrics_text || "")); setShowScriptPanel(true); }}
                  className="flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/50 px-3 py-1.5 text-sm font-bold text-[#00F0FF] hover:bg-[#00F0FF]/10"><FileText size={15} /> Original script</button>
              )}
              <button data-testid="back-to-editor-btn" onClick={() => setTranscriptOpen(false)} className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-sm font-bold text-black hover:bg-[#00F0FF]/90"><ArrowLeft size={15} /> Back to Editor</button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            <div className="flex min-h-0 flex-1 flex-col border-b border-white/10 lg:border-b-0 lg:border-r">
              {previewTabs}
              {playerBlock()}
            </div>
            <div className="flex min-h-0 w-full flex-col lg:w-[440px] xl:w-[480px]">
              {(!hasTranscript || transcribing || showScriptPanel) ? transcribePanel : (
                <>
                  <div className="min-h-0 flex-1">{paragraphBlock}</div>
                  {!hasCaptions && (
                    <div className="shrink-0 border-t border-white/10 p-3">
                      <button data-testid="overlay-create-captions-btn" onClick={() => { createCaptionsNow(); setTranscriptOpen(false); }}
                        className="pulse-glow flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-3 text-base font-bold text-white transition-transform hover:scale-[1.01]">
                        <Layers size={19} /> Create Captions
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : stage === "captions" ? (
        /* CONTENT-FIRST WORKSPACE — Contextual ribbon · Preview (left) + Timeline (right) · floating popups */
        <div className="flex min-h-0 flex-1 flex-col" data-testid="editor-captions-mode">
          <EditorRibbon selectionKind={selectionKind} actions={ribbonActions} onOpenPopup={setActivePopup} />
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            {renderWorkspace()}
            {floatingPopups}
          </div>
        </div>
      ) : stage === "paragraph" ? (
        /* PHASE 1/2 — video centered (left) + editable paragraph transcript (right) */
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row" data-testid="editor-paragraph-mode">
          <div className="flex min-h-0 flex-1 flex-col border-b border-white/10 lg:border-b-0 lg:border-r">
            {previewTabs}
            {playerBlock()}
          </div>
          <div className="flex min-h-0 w-full flex-col lg:w-[420px] xl:w-[460px]">
            {paragraphBlock}
          </div>
        </div>
      ) : (
        /* PHASE 4/5 — video centered (left) + word-by-word transcript + Create Captions (right) */
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row" data-testid="editor-synced-mode">
          <div className="flex min-h-0 flex-1 flex-col border-b border-white/10 lg:border-b-0 lg:border-r">
            {previewTabs}
            {playerBlock()}
            {emojiBlock}
            {createAnimBar}
          </div>
          <div className="flex min-h-0 w-full flex-col lg:w-[420px] xl:w-[460px]">
            <div className="min-h-0 flex-1">{transcriptBlock}</div>
            {createCaptionsBar}
          </div>
        </div>
      )}

      <AnimationModal open={animOpen} onOpenChange={setAnimOpen} config={config} onApply={applyAnimationScoped} segments={segments} projectId={id}
        onApplySavedDesign={(p) => {
          if (!tlActions.current?.applyPresetAll) { toast.error("Open the timeline editor first."); return false; }
          (async () => {
            const mode = await askApplyScope({ what: p.name, ...tlActions.current.scopeInfo() });
            if (mode) tlActions.current.applyPresetScoped(p, mode);
          })();
          return true;
        }} />
      <ApplyScopeDialog />
      <ExportModal open={exportOpen} quick={quickExport} onOpenChange={(o) => { setExportOpen(o); if (!o) setQuickExport(false); }} projectId={id} config={config} captionLayers={captionLayers}
        mediaTracks={mediaTracks} videoUrl={originalVideoUrl} videoMeta={project.video} segments={segments} duration={timelineDuration} fitMode={fitMode} letterbox={letterbox} />
      <PhraseRenderWorkspace open={v2Open} onOpenChange={setV2Open} projectId={id} />
      <LanguageModal open={langOpen} onOpenChange={setLangOpen} projectId={id} isPro={isPro} onApplied={applyLanguage} variant={variant} onReset={revertLanguage} versions={textVersions} activeVersionId={activeVersionId} />
      <SrtModal open={srtOpen} onOpenChange={setSrtOpen} projectId={id} projectName={name} />
      {(uploadModal || uploadState === "error") && (() => {
        const fb = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${(b / 1e6).toFixed(1)} MB`);
        const size = localAsset?.file?.size || 0;
        const fname = localAsset?.file?.name || "your video";
        const done = fb(size * (uploadPct / 100)), tot = fb(size);
        if (uploadState === "error") return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="upload-error-modal">
            <div className="relative w-full max-w-md rounded-2xl border border-red-500/30 bg-[#0d0d0d] p-6 text-center text-white shadow-2xl">
              <WindowSearch />
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15"><AlertTriangle size={24} className="text-red-400" /></div>
              <div className="mb-1 font-display text-lg font-black">Upload failed</div>
              <p className="mb-5 text-sm text-[#A1A1AA]">Your video didn't finish uploading — the connection may have been interrupted. Your file is still here, so you can retry without re-selecting it.</p>
              <div className="flex flex-col gap-2">
                <button onClick={retryUpload} data-testid="upload-retry-btn" className="w-full rounded-xl bg-[#00F0FF] py-2.5 text-sm font-bold text-black hover:bg-[#00F0FF]/90"><RotateCcw size={15} className="mr-1 inline" /> Retry Upload</button>
                <button onClick={cancelUpload} data-testid="upload-error-cancel-btn" className="w-full rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/5">Cancel & Back to Projects</button>
              </div>
            </div>
          </div>
        );
        if (uploadModal === "start") return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="upload-start-modal">
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 text-white shadow-2xl">
              <WindowSearch />
              <div className="mb-1 flex items-center gap-2 font-display text-lg font-black"><Upload size={18} className="text-[#00F0FF]" /> Video Uploading</div>
              <p className="mb-4 text-sm text-[#A1A1AA]">Your video is uploading. You can keep working while it finishes.</p>
              <div className="mb-1 flex items-center justify-between text-xs"><span className="truncate text-white">{fname}</span><span className="font-plex text-[#00F0FF]">{uploadPct}%</span></div>
              <div className="mb-2 h-2.5 w-full overflow-hidden rounded-full bg-white/10"><div className="cm-neon-bar h-full rounded-full transition-[width] duration-300" style={{ width: `${uploadPct}%` }} /></div>
              <div className="mb-5 text-right text-[11px] text-[#666]">{done} / {tot}</div>
              <div className="flex gap-2">
                <button onClick={cancelUpload} data-testid="upload-cancel-btn" className="w-1/3 rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/5">Cancel</button>
                <button onClick={() => setUploadModal(null)} data-testid="upload-start-ok" className="flex-1 rounded-xl bg-[#00F0FF] py-2.5 text-sm font-bold text-black hover:bg-[#00F0FF]/90">Keep editing</button>
              </div>
            </div>
          </div>
        );
        return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="upload-success-modal">
            <div className="relative w-full max-w-md rounded-2xl border border-[#00F0FF]/30 bg-[#0d0d0d] p-6 text-center text-white shadow-2xl">
              <WindowSearch />
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#22c55e]/15"><Check size={24} className="text-[#22c55e]" /></div>
              <div className="mb-1 font-display text-lg font-black">Video Uploaded Successfully</div>
              <p className="mb-5 text-sm text-[#A1A1AA]">Your video is ready to edit. Want captions now?</p>
              <div className="flex flex-col gap-2">
                <button onClick={() => setUploadModal(null)} data-testid="upload-success-transcribe" className="w-full rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-2.5 text-sm font-bold text-white hover:opacity-90"><FileText size={15} className="mr-1 inline" /> Transcribe Video</button>
                <button onClick={() => setUploadModal(null)} data-testid="upload-success-continue" className="w-full rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/5">Continue Editing</button>
              </div>
            </div>
          </div>
        );
      })()}
      {uploadBusy && (() => {
        const fb = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${(b / 1e6).toFixed(1)} MB`);
        const size = localAsset?.file?.size || 0;
        const fname = localAsset?.file?.name || "video";
        return (
          <div data-testid="upload-progress-card" className="cm-neon-chip fixed bottom-4 right-4 z-50 w-72 rounded-xl bg-[#0d0d0d]/95 px-4 py-3 backdrop-blur">
            <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold">
              <span className="flex items-center gap-1.5 truncate text-white"><Upload size={12} className="text-[#00F0FF]" /> {uploadState === "finalizing" ? "Finishing…" : `Uploading ${fname}`}</span>
              <span className="font-plex text-[#00F0FF]">{uploadState === "finalizing" ? "" : `${uploadPct}%`}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/10"><div className="cm-neon-bar h-full rounded-full transition-[width] duration-300" style={{ width: `${uploadState === "finalizing" ? 100 : uploadPct}%` }} /></div>
            {size > 0 && <div className="mt-1.5 text-right text-[10px] text-[#666]">{fb(size * (uploadPct / 100))} / {fb(size)}</div>}
          </div>
        );
      })()}
      {selectedWordId && (() => {
        const sw = flatWords.find((w) => w.word_id === selectedWordId) || (styleWordText ? { word: styleWordText } : null);
        if (!sw) return null;
        return (
          <WordStyleDialog open={styleWordOpen} onOpenChange={setStyleWordOpen}
            config={config} setConfig={(u) => { setConfig(u); setHasAnimation(true); }}
            wordId={selectedWordId} wordText={sw.word}
            isHighlighted={(config.highlightWordIds || []).includes(selectedWordId)}
            copied={copiedStyle} setCopied={setCopiedStyle} />
        );
      })()}
      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[#0d0d0d] text-white" data-testid="usage-dialog">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-black"><Gauge size={20} className="text-[#00F0FF]" /> Your usage</DialogTitle>
          <DialogDescription className="sr-only">Your current plan limits and usage this month.</DialogDescription>
          <UsageMeter />
        </DialogContent>
      </Dialog>
    </div>
  );
}
